"use client";
import { AspectRatio } from "@components/core/AspectRatio";
import dynamic from "next/dynamic";
import { useCallback, useEffect, useRef, useState } from "react";
import { getLocalStorage, setLocalStorage, LocalStorageKeys } from "@utilities/localStorage";

import Orb from "../Orb/Orb";
import { ConvAiOrbReady } from "./ConvAiOrbReady";

const ConvAiOrbInCall = dynamic(() => import("./ConvAiOrbInCall").then(mod => ({ default: mod.ConvAiOrbInCall })), {
  ssr: false,
});

const ConvAITermsModal = dynamic(
  () => import("../../ConvAI/ConvAITermsModal").then(mod => ({ default: mod.ConvAITermsModal })),
  {
    ssr: false,
  },
);

export interface ConvAiOrbProps {
  agentId: string;
  className?: string;
  colors?: string[];
  voiceId?: string;
}

type VolumeFunctions = {
  getInputVolume: () => number;
  getOutputVolume: () => number;
};

export function ConvAiOrb({ voiceId, agentId, colors, className }: ConvAiOrbProps) {
  const [isInCall, setIsInCall] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const volumeFunctionsRef = useRef<VolumeFunctions | null>(null);

  // Stable volume functions that always exist
  const getInputVolume = useCallback(() => {
    return volumeFunctionsRef.current?.getInputVolume() ?? 0;
  }, []);

  const getOutputVolume = useCallback(() => {
    return volumeFunctionsRef.current?.getOutputVolume() ?? 0;
  }, []);

  const handleStartCall = () => {
    // Check if terms have already been accepted
    const termsAccepted = getLocalStorage(LocalStorageKeys.CONVAI_MARKETING_PAGE_TERMS);
    if (termsAccepted) {
      setIsInCall(true);
    } else {
      setShowTerms(true);
    }
  };

  const handleAgreeToTerms = () => {
    setLocalStorage(LocalStorageKeys.CONVAI_MARKETING_PAGE_TERMS, "true");
    setShowTerms(false);
    setIsInCall(true);
  };

  const handleCloseTerms = () => {
    setShowTerms(false);
  };

  const handleEndCall = () => {
    setIsInCall(false);
    // Clear volume functions ref when ending call
    volumeFunctionsRef.current = null;
  };

  // Reset call state when agentId or voiceId changes
  useEffect(() => {
    setIsInCall(false);
    volumeFunctionsRef.current = null;
  }, [agentId, voiceId]);

  return (
    <>
      <AspectRatio className={className} ratio='1x1'>
        <Orb colors={colors} getInputVolume={getInputVolume} getOutputVolume={getOutputVolume} />
        {isInCall ? (
          <ConvAiOrbInCall
            agentId={agentId}
            onEndCall={handleEndCall}
            voiceId={voiceId}
            volumeFunctionsRef={volumeFunctionsRef}
          />
        ) : (
          <ConvAiOrbReady agentId={agentId} onStartCall={handleStartCall} voiceId={voiceId} />
        )}
      </AspectRatio>
      {showTerms && <ConvAITermsModal onAgree={handleAgreeToTerms} onClose={handleCloseTerms} />}
    </>
  );
}
